import { DeepSignal, patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { SignedInUser } from '../../shared/types/user';
import { Chain } from '../../shared/types/chain';
import { CurrencyInfo } from '../../shared/types/currency';
import { Toast } from '../../shared/types/toast';
import { Popup } from '../../shared/components/popup/popup';
import { timer } from 'rxjs';

export interface ApplicationState {
    signedInUser: SignedInUser | null;
    isJuyoUser: boolean;
    chains: Chain[];
    selectedChain: Chain | null;
    currencyInfo: CurrencyInfo | null;
    toasts: Toast[];
    popups: Popup[];
}

const initialState: ApplicationState = {
    signedInUser: null,
    isJuyoUser: false,
    chains: [],
    selectedChain: null,
    currencyInfo: null,
    toasts: [],
    popups: [],
};

export interface ApplicationStoreType {
    signedInUser: DeepSignal<SignedInUser | null>;
    isJuyoUser: DeepSignal<boolean>;
    chains: DeepSignal<Chain[]>;
    selectedChain: DeepSignal<Chain | null>;
    currencyInfo: DeepSignal<CurrencyInfo | null>;
    toasts: DeepSignal<Toast[]>;
    popups: DeepSignal<Popup[]>;

    resetState: () => void;
    setSignedInUser: (user: SignedInUser | null) => void;
    setChains: (chains: Chain[]) => void;
    setSelectedChain: (chain: Chain) => void;
    setCurrencyInfo: (currencyInfo: CurrencyInfo) => void;
    addToast: (toast: Toast) => void;
    addTimedToast: (toast: Toast, timeMilliseconds: number) => void;
    removeToast: (toast: Toast) => void;
    showPopup: (popup: Popup) => void;
    closePopup: (id: string) => void;
}

export const ApplicationStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store) => ({
        resetState(): void {
            patchState(store, initialState);
        },
        setSignedInUser(signedInUser: SignedInUser | null): void {
            const isJuyoUser = !!signedInUser?.email.includes('@juyoanalytics.com');
            patchState(store, { signedInUser, isJuyoUser });
        },
        setChains(chains: Chain[]): void {
            patchState(store, { chains });
        },
        setSelectedChain(chain: Chain): void {
            patchState(store, { selectedChain: chain });
        },
        setCurrencyInfo(currencyInfo: CurrencyInfo): void {
            patchState(store, { currencyInfo });
        },
        addToast(toast: Toast): void {
            const toasts = store.toasts().concat(toast);
            patchState(store, { toasts });
        },
        addTimedToast(toast: Toast, timeMilliseconds = 2500): void {
            this.addToast(toast);
            timer(timeMilliseconds).subscribe(() => this.removeToast(toast));
        },
        removeToast(toast: Toast): void {
            const toasts = store.toasts().filter((t) => t.id !== toast.id);
            patchState(store, { toasts });
        },
        showPopup(popup: Popup): void {
            const popups = store.popups().concat(popup);
            patchState(store, { popups });
        },
        closePopup(id: string): void {
            const popups = store.popups().filter((p) => p.id !== id);
            patchState(store, { popups });
        },
    })),
);
